/**
 * services/StrategyService.ts
 * Service for managing trading strategies
 *
 * Strategies are philosophical containers that group related playbooks under
 * a unified trading approach. Each strategy has its own philosophy and risk profile.
 *
 * Extends BaseService to provide CRUD operations with status-based filtering
 * and playbook linkage queries.
 */

import { BaseService } from './BaseService';
import type { Strategy } from '@/types/schema/Strategy';
import type { EntityStatus } from '@/types/shared/EntityStatus';
import { isEntityActive } from '@/types/shared/EntityStatus';

/**
 * Service for managing trading strategies
 *
 * Provides CRUD operations plus status-based filtering and playbook linkage.
 * Inherits getAll(), get(), update(), softDelete() from BaseService.
 */
export class StrategyService extends BaseService<Strategy> {
  constructor() {
    super('strategies');
  }

  /**
   * Add a new strategy with default status
   *
   * Defaults to status: 'active' if not provided.
   *
   * @param data - Partial strategy data
   * @returns Promise resolving to the created strategy
   */
  override async add(data: Partial<Strategy>): Promise<Strategy> {
    return super.add({ status: 'active', ...data });
  }

  /**
   * Get strategies by status
   *
   * Filters strategies by the specified EntityStatus.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @param status - The status to filter by ('active' | 'inactive' | 'archived')
   * @returns Promise resolving to array of strategies with matching status
   */
  async getByStatus(status: EntityStatus): Promise<Strategy[]> {
    const allStrategies = await this.getAll();
    return allStrategies.filter(strategy => strategy.status === status);
  }

  /**
   * Get all active strategies
   *
   * Convenience method for getting strategies where status is 'active'.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @returns Promise resolving to array of active strategies
   */
  async getActiveStrategies(): Promise<Strategy[]> {
    const allStrategies = await this.getAll();
    return allStrategies.filter(strategy => isEntityActive(strategy.status));
  }

  /**
   * Get playbook IDs linked to a strategy
   *
   * Returns the playbookIds array from the strategy, which is the source
   * of truth for strategy→playbook linkage.
   *
   * @param strategyId - The strategy ID to get playbook IDs for
   * @returns Promise resolving to array of playbook IDs (empty if strategy not found)
   */
  async getPlaybookIds(strategyId: string): Promise<string[]> {
    const strategy = await this.get(strategyId);
    return strategy?.playbookIds ?? [];
  }
}

// Export singleton instance
export const strategyService = new StrategyService();
